/**
 * Tabbed case detail: Overview · Documents · Field verification · Approvals ·
 * Disbursement · Delivery · Audit. Phase 3 makes Approvals/Disbursement/Delivery
 * actionable: tiered sign workflow, ledger entry recording, delivery acknowledgement.
 * Field verification (Phase 4) shows synced data or a locked state.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, MapPin, ShieldCheck, Wallet, PackageCheck, ScrollText,
  LayoutPanelTop, AlertTriangle, ChevronRight, Lock, Image as ImageIcon, PenLine, Banknote, Building2, Landmark, Upload, Check,
  MessageSquare, Send, Eye, EyeOff, Trash2,
} from 'lucide-react';

// Resolve a stored upload path to an openable URL. In dev '/uploads/..' is Vite-proxied;
// in prod it lives on the API origin (VITE_API_BASE_URL).
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
function fileUrl(u) {
  if (!u) return u;
  if (/^https?:\/\//.test(u)) return u;
  try {
    if (/^https?:\/\//.test(API_BASE)) return `${new URL(API_BASE).origin}${u}`;
  } catch {
    /* fall through */
  }
  return u;
}

const METHOD_LABEL = {
  bank_transfer: 'Bank transfer',
  manual_cash: 'Manual cash',
  vendor_direct: 'Vendor-direct',
};
import { api } from '../../lib/apiClient.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { useToast } from '../../components/feedback/ToastProvider.jsx';
import { useDialog } from '../../components/feedback/DialogProvider.jsx';
import { StatusBadge } from '../../components/ui/StatusBadge.jsx';
import { Lightbox } from '../../components/ui/Lightbox.jsx';
import { Field, TextInput, Select } from '../../components/ui/Field.jsx';
import { CATEGORY_META, formatINR, formatDate, STATUS_META } from '../../features/cases/caseMeta.js';
import styles from './CaseDetail.module.css';

const NEXT_STATUS = { intake: 'field_verify', field_verify: 'pending_approval', delivered: 'closed' };

const TABS = [
  { key: 'overview', label: 'Overview', icon: LayoutPanelTop },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'field', label: 'Field verification', icon: MapPin },
  { key: 'approvals', label: 'Approvals', icon: ShieldCheck },
  { key: 'disbursement', label: 'Disbursement', icon: Wallet },
  { key: 'delivery', label: 'Delivery', icon: PackageCheck },
  { key: 'notes', label: 'Notes', icon: MessageSquare },
  { key: 'audit', label: 'Audit', icon: ScrollText },
];

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const toast = useToast();
  const { confirm } = useDialog();
  const [tab, setTab] = useState('overview');
  const [state, setState] = useState({ status: 'loading' });
  const [busy, setBusy] = useState(false);
  // Dual-approval threshold comes from Settings (super_admin-configurable), not a constant.
  const [threshold, setThreshold] = useState(25000);

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', data: await api.get(`/cases/${id}`) });
    } catch (err) {
      setState({ status: 'error', error: err.message });
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get('/settings')
      .then((s) => { if (s?.approvalThreshold != null) setThreshold(Number(s.approvalThreshold)); })
      .catch(() => {});
  }, []);

  const advance = async () => {
    const next = NEXT_STATUS[state.data.status];
    if (!next) return;
    setBusy(true);
    try {
      await api.patch(`/cases/${id}/status`, { status: next });
      toast.success(`Moved to ${STATUS_META[next].label}.`);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    const ok = await confirm({
      title: 'Reject this case?',
      message: 'It will be marked rejected and removed from the active pipeline.',
      confirmLabel: 'Reject case',
      danger: true,
    });
    if (!ok) return;
    const reason = window.prompt('Reason for rejection:')?.trim();
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/cases/${id}/reject`, { reason });
      toast.success('Case rejected.');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    const ok = await confirm({
      title: `Delete ${state.data.caseNo}?`,
      message: 'This permanently removes the case and its field-verification, disbursement and delivery records. The audit-log entry is kept. This cannot be undone.',
      confirmLabel: 'Delete case',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.del(`/cases/${id}`);
      toast.success('Case deleted.');
      navigate('/cases');
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  const togglePublic = async () => {
    const publishing = !state.data.isPublic;
    if (publishing) {
      const ok = await confirm({
        title: 'Publish to the public wall?',
        message: 'This closed case will appear on the public Served wall (PII-free — only initials, village, amount and field photos). Confirm the beneficiary consented to public listing.',
        confirmLabel: 'Publish',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.patch(`/cases/${id}/visibility`, { isPublic: publishing });
      toast.success(publishing ? 'Published to the public wall.' : 'Removed from the public wall.');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (state.status === 'loading') return <div className={styles.bootCard} />;
  if (state.status === 'error')
    return (
      <div className={styles.errorWrap}>
        <p>{state.error}</p>
        <Link to="/cases">← Back to pipeline</Link>
      </div>
    );

  const c = state.data;
  const Cat = CATEGORY_META[c.category]?.icon;
  const canAdvance = Boolean(NEXT_STATUS[c.status]) && hasRole('office_staff');
  const canReject =
    !['closed', 'rejected', 'delivered', 'disbursed'].includes(c.status) &&
    hasRole('approver', 'senior_approver');

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate('/cases')}>
        <ArrowLeft size={16} /> Pipeline
      </button>

      <header className={styles.header}>
        <div>
          <div className={styles.titleRow}>
            <h1 className="mono">{c.caseNo}</h1>
            <StatusBadge status={c.status} />
            {c.dupFlags?.length > 0 && (
              <span className={styles.flag}>
                <AlertTriangle size={14} /> {c.dupFlags.length} duplicate flag(s)
              </span>
            )}
          </div>
          <p className={styles.sub}>
            {c.beneficiary?.name} · {c.beneficiary?.village}, {c.beneficiary?.block} ·{' '}
            {Cat && <Cat size={14} />} {CATEGORY_META[c.category]?.label} ({c.flow})
          </p>
        </div>
        <div className={styles.actions}>
          {canAdvance && (
            <button className={styles.primary} onClick={advance} disabled={busy}>
              Move to {STATUS_META[NEXT_STATUS[c.status]].label} <ChevronRight size={16} />
            </button>
          )}
          {canReject && (
            <button className={styles.danger} onClick={reject} disabled={busy}>
              Reject
            </button>
          )}
          {c.status === 'closed' && hasRole('office_staff') && (
            <button className={c.isPublic ? styles.danger : styles.primary} onClick={togglePublic} disabled={busy}>
              {c.isPublic ? <><EyeOff size={16} /> Remove from wall</> : <><Eye size={16} /> Publish to wall</>}
            </button>
          )}
          {hasRole('super_admin') && (
            <button className={styles.danger} onClick={del} disabled={busy}>
              <Trash2 size={16} /> Delete
            </button>
          )}
        </div>
      </header>

      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.key} className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`} onClick={() => setTab(t.key)}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </nav>

      <div className={styles.tabBody}>
        {tab === 'overview' && <OverviewTab c={c} onReload={load} />}
        {tab === 'documents' && <DocumentsTab beneficiary={c.beneficiary} onReload={load} />}
        {tab === 'field' && <FieldTab fv={c.fieldVerification} />}
        {tab === 'approvals' && <ApprovalsTab c={c} threshold={threshold} onReload={load} />}
        {tab === 'disbursement' && <DisbursementTab c={c} onReload={load} />}
        {tab === 'delivery' && <DeliveryTab c={c} onReload={load} />}
        {tab === 'notes' && <NotesTab c={c} onReload={load} />}
        {tab === 'audit' && <AuditTab timeline={c.auditTimeline} />}
      </div>
    </div>
  );
}

function AssignAgent({ c, onReload }) {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);
  const canAssign = hasRole('office_staff');

  useEffect(() => {
    if (!canAssign) return;
    api.get('/cases/field-agents').then(setAgents).catch(() => setAgents([]));
  }, [canAssign]);

  const change = async (agentId) => {
    setBusy(true);
    try {
      await api.patch(`/cases/${c.id}/assign`, { agentId: agentId || null });
      toast.success(agentId ? 'Field agent assigned.' : 'Assignment cleared.');
      onReload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!canAssign) {
    return <Row label="Field agent" value={c.assignedAgentName || 'Unassigned (shared)'} />;
  }
  return (
    <div>
      <dt>Field agent</dt>
      <dd>
        <select
          className={styles.assignSelect}
          value={c.assignedAgent || ''}
          onChange={(e) => change(e.target.value)}
          disabled={busy}
        >
          <option value="">Unassigned (shared queue)</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </dd>
    </div>
  );
}

function OverviewTab({ c, onReload }) {
  return (
    <div className={styles.grid2}>
      <div className={styles.card}>
        <h3>Case</h3>
        <dl className={styles.kv}>
          <Row label="Requested" value={formatINR(c.amountRequested)} mono />
          <Row label="Approved" value={c.amountApproved != null ? formatINR(c.amountApproved) : '—'} mono />
          <Row label="Flow" value={c.flow} />
          <Row label="Intake by" value={c.intakeByName || '—'} />
          <Row label="Created" value={formatDate(c.createdAt)} />
          <Row label="Status since" value={formatDate(c.statusChangedAt)} />
          <Row label="Public wall" value={c.isPublic ? 'Published' : 'Not published'} />
          <AssignAgent c={c} onReload={onReload} />
        </dl>
        {c.summary && <p className={styles.summary}>{c.summary}</p>}
        {c.rejectedReason && <p className={styles.rejected}>Rejected: {c.rejectedReason}</p>}
      </div>
      <div className={styles.card}>
        <h3>Beneficiary</h3>
        <dl className={styles.kv}>
          <Row label="Name" value={c.beneficiary?.name} />
          <Row label="Phone" value={c.beneficiary?.phone} mono />
          <Row label="Village" value={c.beneficiary?.village} />
          <Row label="Block" value={c.beneficiary?.block} />
          <Row label="Address" value={c.beneficiary?.address || '—'} />
          <Row label="Public on close" value={c.beneficiary?.consent?.publicAfterClose ? 'Consented' : 'Not consented'} />
        </dl>
      </div>
    </div>
  );
}

const DOC_DEFS = [
  { key: 'aadhaarDoc', label: 'Aadhaar' },
  { key: 'casteCertDoc', label: 'Caste certificate' },
  { key: 'incomeProofDoc', label: 'Income proof' },
  { key: 'photo', label: 'Beneficiary photo' },
];

function DocumentsTab({ beneficiary, onReload }) {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [busyKey, setBusyKey] = useState(null);
  const canEdit = hasRole('office_staff');
  const presentCount = DOC_DEFS.filter((d) => beneficiary?.[d.key]).length;

  const upload = async (key, file) => {
    if (!file || !beneficiary?.id) return;
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append('files', file);
      const res = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.patch(`/beneficiaries/${beneficiary.id}`, { [key]: res.url });
      toast.success('Document uploaded.');
      onReload?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyKey(null);
    }
  };

  if (presentCount === 0 && !canEdit) {
    return <EmptyState icon={ImageIcon} title="No documents on file" desc="Intake documents attach here when uploaded." />;
  }

  return (
    <div className={styles.docGrid}>
      {DOC_DEFS.map((d) => {
        const raw = beneficiary?.[d.key];
        const url = raw ? fileUrl(raw) : null;
        const isPdf = raw && /\.pdf($|\?)/i.test(raw);
        return (
          <div key={d.key} className={styles.docSlot}>
            {url ? (
              <a className={styles.docCard} href={url} target="_blank" rel="noreferrer" title={`Open ${d.label}`}>
                <div
                  className={styles.docThumb}
                  style={isPdf ? undefined : { backgroundImage: `url(${url})` }}
                >
                  {isPdf && <FileText size={26} />}
                </div>
                <span>{d.label}</span>
              </a>
            ) : (
              <div className={`${styles.docCard} ${styles.docEmpty}`}>
                <div className={styles.docThumb}>
                  <ImageIcon size={22} />
                </div>
                <span>{d.label}</span>
              </div>
            )}
            <div className={styles.docActions}>
              {url && (
                <a className={styles.docViewBtn} href={url} target="_blank" rel="noreferrer">
                  <Eye size={13} /> View
                </a>
              )}
              {canEdit && (
                <label className={`${styles.docUploadBtn} ${busyKey === d.key ? styles.docUploadBusy : ''}`}>
                  <Upload size={13} /> {busyKey === d.key ? 'Uploading…' : url ? 'Replace' : 'Upload'}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    disabled={busyKey === d.key}
                    onChange={(e) => upload(d.key, e.target.files[0])}
                  />
                </label>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Approvals — tiered sign workflow ────────────────────────────────────── */
function ApprovalsTab({ c, threshold = 25000, onReload }) {
  const { hasRole, user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const amount = c.amountApproved ?? c.amountRequested;
  const required = amount >= threshold ? 2 : 1;
  const collected = c.approvals?.length || 0;
  const alreadySigned = c.approvals?.some((a) => String(a.by) === user?.id);
  const canSign =
    c.status === 'pending_approval' && hasRole('approver', 'senior_approver') && !alreadySigned;

  const sign = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/cases/${c.id}/approve`, {});
      toast.success(res.approved ? 'Case approved.' : `Signed — ${res.signaturesCollected}/${res.signaturesRequired}.`);
      onReload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.tierHead}>
        <div>
          <h3>Tiered approval</h3>
          <p className={styles.tierNote}>
            {amount >= threshold
              ? `Amount ≥ ${formatINR(threshold)} — two distinct approvers required.`
              : `Amount below ${formatINR(threshold)} — a single approver suffices.`}
          </p>
        </div>
        <div className={styles.tierCount}>
          <span className="mono">{collected}/{required}</span> signed
        </div>
      </div>

      <div className={styles.sigTrack}>
        {Array.from({ length: required }).map((_, i) => {
          const a = c.approvals?.[i];
          return (
            <div key={i} className={`${styles.sigSlot} ${a ? styles.sigDone : ''}`}>
              {a ? (
                <>
                  <ShieldCheck size={18} />
                  <div>
                    <strong>{a.byName}</strong>
                    <span className={styles.tag}>{a.role}</span>
                    <span className={styles.sub}>{formatDate(a.at)}</span>
                  </div>
                  <code className={styles.hash}>{a.signatureHash?.slice(0, 10)}…</code>
                </>
              ) : (
                <>
                  <PenLine size={18} />
                  <span className={styles.awaiting}>Awaiting signature {i + 1}</span>
                </>
              )}
            </div>
          );
        })}
      </div>

      {canSign && (
        <button className={styles.signBtn} onClick={sign} disabled={busy}>
          <PenLine size={16} /> {busy ? 'Signing…' : 'Sign approval (identity + timestamp logged)'}
        </button>
      )}
      {alreadySigned && c.status === 'pending_approval' && (
        <p className={styles.signedNote}>You have signed. Awaiting the second approver.</p>
      )}
      {c.status !== 'pending_approval' && collected > 0 && (
        <p className={styles.signedNote}>Approval complete — {formatINR(c.amountApproved)} approved.</p>
      )}
    </div>
  );
}

/* ── Disbursement — record ledger entry ──────────────────────────────────── */
function DisbursementTab({ c, onReload }) {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [method, setMethod] = useState('bank_transfer');
  const [vendorName, setVendorName] = useState('');
  const [proofDoc, setProofDoc] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [busy, setBusy] = useState(false);

  if (c.disbursement) {
    const d = c.disbursement;
    return (
      <div className={styles.card}>
        <h3>Disbursement</h3>
        <dl className={styles.kv}>
          <Row label="Method" value={METHOD_LABEL[d.method] || d.method} />
          <Row label="Amount" value={formatINR(d.amount)} mono />
          <Row label="Vendor" value={d.vendorName || '—'} />
          <Row label="By" value={d.byName || '—'} />
          <Row label="Date" value={formatDate(d.at)} />
          <Row label="Txn reference" value={d.txnRef || '—'} mono />
        </dl>
        {d.proofDoc && (
          <a href={d.proofDoc} target="_blank" rel="noreferrer" className={styles.proofLink}>
            <FileText size={15} /> View proof
          </a>
        )}
      </div>
    );
  }

  if (c.status !== 'approved')
    return <EmptyState icon={Wallet} title="Not ready for disbursement" desc="A case must be approved before it can be disbursed." locked />;
  if (!hasRole('office_staff'))
    return <EmptyState icon={Lock} title="Office staff only" desc="Recording disbursements is an office-staff action." locked />;

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('files', file);
    try {
      const res = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProofDoc(res.url);
      toast.success('Proof uploaded.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submit = async () => {
    if (method !== 'bank_transfer' && !proofDoc) return toast.error('Upload a proof document.');
    if (method === 'vendor_direct' && !vendorName) return toast.error('Vendor name required.');
    setBusy(true);
    try {
      await api.post(`/disbursements/cases/${c.id}`, {
        method,
        vendorName: vendorName || undefined,
        proofDoc: proofDoc || undefined,
        txnRef: txnRef || undefined,
      });
      toast.success('Disbursement recorded.');
      onReload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.card}>
      <h3>Record disbursement</h3>
      <p className={styles.tierNote}>
        Approved amount: {formatINR(c.amountApproved)} — payment is collected and paid out manually.
      </p>
      <div className={styles.methodPick}>
        {[
          { k: 'bank_transfer', label: 'Bank transfer', icon: Landmark },
          { k: 'manual_cash', label: 'Manual cash', icon: Banknote },
          { k: 'vendor_direct', label: 'Vendor-direct', icon: Building2 },
        ].map((m) => (
          <button key={m.k} className={`${styles.methodOpt} ${method === m.k ? styles.methodActive : ''}`} onClick={() => setMethod(m.k)}>
            <m.icon size={18} /> {m.label}
          </button>
        ))}
      </div>
      {method === 'vendor_direct' && (
        <Field label="Vendor name" required>
          <TextInput value={vendorName} onChange={(e) => setVendorName(e.target.value)} placeholder="Ration shop / hospital" />
        </Field>
      )}
      {method === 'bank_transfer' && (
        <Field label="Transaction reference (optional)">
          <TextInput value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder="UTR / UPI ref" />
        </Field>
      )}
      {method !== 'bank_transfer' && (
        <Field label="Proof document" required>
          {proofDoc ? (
            <span className={styles.uploadedTag}><Check size={14} /> Uploaded</span>
          ) : (
            <label className={styles.uploadBtn}>
              <Upload size={15} /> Upload proof
              <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => upload(e.target.files[0])} />
            </label>
          )}
        </Field>
      )}
      <button className={styles.signBtn} onClick={submit} disabled={busy}>
        {busy ? 'Recording…' : 'Record disbursement'}
      </button>
    </div>
  );
}

/* ── Delivery — acknowledgement ──────────────────────────────────────────── */
function DeliveryTab({ c, onReload }) {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [ackType, setAckType] = useState('signature');
  const [ackProof, setAckProof] = useState('');
  const [volunteerId, setVolunteerId] = useState('');
  const [busy, setBusy] = useState(false);

  if (c.delivery) {
    const d = c.delivery;
    return (
      <div className={styles.card}>
        <h3>Delivery</h3>
        <dl className={styles.kv}>
          <Row label="Acknowledgement" value={d.ackType} />
          <Row label="Volunteer" value={d.volunteerId || '—'} />
          <Row label="Delivered by" value={d.byName || '—'} />
          <Row label="Delivered at" value={formatDate(d.deliveredAt)} />
        </dl>
        {d.checklist && Object.keys(d.checklist).length > 0 && (
          <ul className={styles.noteList} style={{ marginTop: 'var(--space-4)' }}>
            {Object.entries(d.checklist).map(([label, val]) => (
              <li key={label} className={styles.noteItem} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {val ? <Check size={16} color="var(--green-600)" /> : <Lock size={14} />}
                <span>{label}</span>
              </li>
            ))}
          </ul>
        )}
        {d.completionPhotos?.length > 0 && (
          <>
            <p className={styles.tierNote} style={{ marginTop: 'var(--space-4)', fontWeight: 600 }}>Completion (after) photos</p>
            <div className={styles.docGrid} style={{ marginTop: 'var(--space-2)' }}>
              {d.completionPhotos.map((url, i) => (
                <a key={i} className={styles.docCard} href={fileUrl(url)} target="_blank" rel="noreferrer">
                  <div className={styles.docThumb} style={{ backgroundImage: `url(${fileUrl(url)})` }} />
                  <span>After {i + 1}</span>
                </a>
              ))}
            </div>
          </>
        )}
        {d.ackProof && (
          <a href={d.ackProof} target="_blank" rel="noreferrer" className={styles.proofLink} style={{ marginTop: 'var(--space-4)' }}>
            <FileText size={15} /> View acknowledgement
          </a>
        )}
      </div>
    );
  }

  if (c.status !== 'disbursed')
    return <EmptyState icon={PackageCheck} title="Not ready for delivery" desc="A case must be disbursed before delivery is recorded. The field app also records this (Phase 4)." locked />;
  if (!hasRole('office_staff', 'field_agent'))
    return <EmptyState icon={Lock} title="Field / office staff only" desc="Recording delivery is a field or office action." locked />;

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('files', file);
    try {
      const res = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAckProof(res.url);
      toast.success('Acknowledgement uploaded.');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/cases/${c.id}`, {
        ackType,
        ackProof: ackProof || undefined,
        volunteerId: volunteerId || undefined,
      });
      toast.success('Delivery recorded.');
      onReload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.card}>
      <h3>Record delivery</h3>
      <Field label="Acknowledgement type">
        <Select value={ackType} onChange={(e) => setAckType(e.target.value)}>
          <option value="signature">Signature</option>
          <option value="thumbprint">Thumbprint</option>
          <option value="otp">OTP confirm</option>
        </Select>
      </Field>
      <Field label="Volunteer / delivery ID">
        <TextInput value={volunteerId} onChange={(e) => setVolunteerId(e.target.value)} placeholder="VOL-101" />
      </Field>
      <Field label="Acknowledgement proof">
        {ackProof ? (
          <span className={styles.uploadedTag}><Check size={14} /> Uploaded</span>
        ) : (
          <label className={styles.uploadBtn}>
            <Upload size={15} /> Upload proof
            <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => upload(e.target.files[0])} />
          </label>
        )}
      </Field>
      <button className={styles.signBtn} onClick={submit} disabled={busy}>
        {busy ? 'Recording…' : 'Record delivery'}
      </button>
    </div>
  );
}

/* ── Notes — internal staff working notes ────────────────────────────────── */
function NotesTab({ c, onReload }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const notes = [...(c.notes || [])].sort((a, b) => new Date(b.at) - new Date(a.at));

  const submit = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (body.length < 1) return;
    setBusy(true);
    try {
      await api.post(`/cases/${c.id}/notes`, { text: body });
      setText('');
      toast.success('Note added.');
      onReload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.card}>
      <h3>Internal notes</h3>
      <p className={styles.tierNote}>
        Working notes for staff only — never shown on the public wall. Each note records who
        wrote it and when.
      </p>

      <form className={styles.noteForm} onSubmit={submit}>
        <textarea
          className={styles.noteInput}
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a note about this case…"
          maxLength={2000}
        />
        <button className={styles.signBtn} type="submit" disabled={busy || !text.trim()}>
          <Send size={15} /> {busy ? 'Saving…' : 'Add note'}
        </button>
      </form>

      {notes.length === 0 ? (
        <p className={styles.sub} style={{ marginTop: 16 }}>No notes yet.</p>
      ) : (
        <ul className={styles.noteList}>
          {notes.map((n, i) => (
            <li key={n._id || i} className={styles.noteItem}>
              <div className={styles.noteHead}>
                <strong>{n.byName || 'Staff'}</strong>
                {n.role && <span className={styles.tag}>{n.role}</span>}
                <span className={styles.sub}>{formatDate(n.at)}</span>
              </div>
              <p className={styles.noteText}>{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AuditTab({ timeline }) {
  const toast = useToast();
  const [verify, setVerify] = useState(null);
  const run = async () => {
    try {
      const res = await api.get('/audit/verify');
      setVerify(res);
      toast[res.ok ? 'success' : 'error'](res.ok ? `Chain intact — ${res.verifiedEntries} entries.` : `Broken at #${res.brokenAtSeq}.`);
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div className={styles.card}>
      <div className={styles.auditHead}>
        <h3>Proof-chain timeline</h3>
        <button className={styles.verifyBtn} onClick={run}>
          <ShieldCheck size={15} /> Verify chain
        </button>
      </div>
      {verify && (
        <div className={verify.ok ? styles.verifyOk : styles.verifyBad}>
          {verify.ok ? `Hash chain intact — ${verify.verifiedEntries} entries.` : `Tampering at #${verify.brokenAtSeq}.`}
        </div>
      )}
      <ol className={styles.timeline}>
        {(timeline || []).map((a) => (
          <li key={a.id || a.seq} className={styles.timelineItem}>
            <span className={styles.timelineDot} />
            <div>
              <strong className="mono">{a.action}</strong>
              <span className={styles.sub}>{formatDate(a.at)} · {a.actor?.name || 'system'} · #{a.seq}</span>
            </div>
          </li>
        ))}
        {(!timeline || timeline.length === 0) && <p className={styles.sub}>No audit entries yet.</p>}
      </ol>
    </div>
  );
}

/* ── Field verification — synced from the field app ──────────────────────── */
function FieldTab({ fv }) {
  const [lightbox, setLightbox] = useState(null);

  if (!fv || (!fv.id && !fv.offlineId)) {
    return (
      <EmptyState
        icon={MapPin}
        title="Not verified yet"
        desc="GPS-tagged before/after photos and the checklist appear here once a field agent captures and syncs the verification from the field app."
        locked
      />
    );
  }

  const before = (fv.beforePhotos || []).map((url, i) => ({ url, label: `Before ${i + 1}` }));
  const after = (fv.afterPhotos || []).map((url, i) => ({ url, label: `After ${i + 1}` }));
  const photos = [...before, ...after];
  const gps = fv.gps || {};
  const hasGps = gps.lat != null && gps.lng != null;
  const checklist = fv.checklist && typeof fv.checklist === 'object' ? Object.entries(fv.checklist) : [];

  return (
    <div className={styles.card}>
      <div className={styles.auditHead}>
        <h3>Field verification</h3>
        <span className={styles.signedNote} style={{ margin: 0 }}>
          {fv.verifiedByName ? `By ${fv.verifiedByName}` : ''}{fv.capturedAt ? ` · ${formatDate(fv.capturedAt)}` : ''}
        </span>
      </div>

      <dl className={styles.kv}>
        <Row label="GPS" value={hasGps ? `${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)}${gps.accuracy != null ? ` (±${Math.round(gps.accuracy)}m)` : ''}` : '—'} mono />
        <Row label="Captured" value={fv.capturedAt ? formatDate(fv.capturedAt) : '—'} />
        <Row label="Synced" value={fv.syncedAt ? formatDate(fv.syncedAt) : '—'} />
        <Row label="Photos" value={`${photos.length}`} />
      </dl>

      {hasGps && (
        <a
          className={styles.proofLink}
          href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`}
          target="_blank"
          rel="noreferrer"
        >
          <MapPin size={15} /> Open location on map
        </a>
      )}

      {checklist.length > 0 && (
        <ul className={styles.noteList} style={{ marginTop: 'var(--space-5)' }}>
          {checklist.map(([label, val]) => (
            <li key={label} className={styles.noteItem} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {val ? <Check size={16} color="var(--green-600)" /> : <Lock size={14} />}
              <span>{label}</span>
            </li>
          ))}
        </ul>
      )}

      {fv.notes ? <p className={styles.summary} style={{ marginTop: 'var(--space-4)' }}>{fv.notes}</p> : null}

      {photos.length > 0 ? (
        <>
          <div className={styles.docGrid} style={{ marginTop: 'var(--space-5)' }}>
            {photos.map((d, i) => (
              <button key={d.label} className={styles.docCard} onClick={() => setLightbox(i)}>
                <div className={styles.docThumb} style={{ backgroundImage: `url(${d.url})` }} />
                <span>{d.label}</span>
              </button>
            ))}
          </div>
          <Lightbox images={photos} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
        </>
      ) : (
        <p className={styles.signedNote}>No photos attached to this verification.</p>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, locked }) {
  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>{locked ? <Lock size={26} /> : <Icon size={26} />}</div>
      <h3>{title}</h3>
      <p>{desc}</p>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? 'mono' : ''}>{value}</dd>
    </div>
  );
}
